import type { RichTreeItem } from '../../types';
import { isPlaceholder, isShowMore } from '../../utils/treeId';

function isSyntheticChild(id: string): boolean {
  return isPlaceholder(id) || isShowMore(id);
}

function removeNode(items: RichTreeItem[], nodeId: string): { items: RichTreeItem[]; node: RichTreeItem | null } {
  let removed: RichTreeItem | null = null;
  let changed = false;
  const next: RichTreeItem[] = [];

  for (const item of items) {
    if (item.id === nodeId) {
      removed = item;
      changed = true;
      continue;
    }

    if (item.children) {
      const childResult = removeNode(item.children, nodeId);
      if (childResult.node) {
        removed = childResult.node;
        changed = true;
        const children = childResult.items;
        next.push(children.length > 0 ? { ...item, children } : { ...item, children: undefined });
        continue;
      }
    }

    next.push(item);
  }

  return { items: changed ? next : items, node: removed };
}

function insertAtTopOfParent(items: RichTreeItem[], parentId: string | null, node: RichTreeItem): RichTreeItem[] {
  if (parentId === null) {
    if (items[0]?.id === node.id) return items;
    return [node, ...items.filter((item) => item.id !== node.id)];
  }

  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.id === parentId) {
      const children = item.children ?? [];
      const realChildren = children.filter((child) => !isSyntheticChild(child.id));
      if (realChildren[0]?.id === node.id) return items;

      const syntheticChildren = children.filter((child) => isSyntheticChild(child.id));
      const updated = { ...item, children: [node, ...realChildren, ...syntheticChildren] };
      return [...items.slice(0, i), updated, ...items.slice(i + 1)];
    }

    if (item.children) {
      const nextChildren = insertAtTopOfParent(item.children, parentId, node);
      if (nextChildren !== item.children) {
        return [...items.slice(0, i), { ...item, children: nextChildren }, ...items.slice(i + 1)];
      }
    }
  }

  return items;
}

function findNodeLocation(
  items: RichTreeItem[],
  nodeId: string,
  parentId: string | null = null
): { parentId: string | null; index: number } | null {
  for (let index = 0; index < items.length; index++) {
    const item = items[index];
    if (item.id === nodeId) return { parentId, index };
    if (item.children) {
      const found = findNodeLocation(item.children, nodeId, item.id);
      if (found) return found;
    }
  }
  return null;
}

/**
 * Returns a copy of the tree with `moveNodeId` removed and re-inserted as the
 * first child of `targetParentId` (`null` = first root).
 *
 * Tree ids are kept stable so MUI can reconcile items without remounting the
 * whole tree on every preview update.
 */
export function applyMovePreview(
  items: RichTreeItem[],
  moveNodeId: string,
  targetParentId: string | null
): RichTreeItem[] {
  const location = findNodeLocation(items, moveNodeId);
  if (!location) {
    return items;
  }

  if (location.parentId === targetParentId && location.index === 0) {
    return items;
  }

  const { items: withoutNode, node } = removeNode(items, moveNodeId);
  if (!node) {
    return items;
  }

  return insertAtTopOfParent(withoutNode, targetParentId, node);
}
